import type { IntegrationProviders } from '@vy/types';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import type { Nullable, Optional, ZodSchema } from '@vy/utils';
import { useActivatedIntegration } from '../../../../hooks/use-activated-integration';
import {
  ConfigureAnrokComponents,
  RegisterToAnrokComponents,
} from '../NEW-integrations-page-components/anrok-components';
import type { FormDrawerProps } from '../NEW-integrations-page-components/FormDrawer';
import { ConfigureHubSpotComponents } from '../NEW-integrations-page-components/hubspot-components/configure-hubspot-integration';
import { RegisterToHubspotComponents } from '../NEW-integrations-page-components/hubspot-components/register-to-hubspot';
import { ConfigureMorningComponents } from '../NEW-integrations-page-components/morning-components/configure-morning-integration';
import { RegisterToMorningComponents } from '../NEW-integrations-page-components/morning-components/register-to-morning';
import { ConfigureNetSuiteComponents } from '../NEW-integrations-page-components/net-suite-components/configure-net-suite-integration';
import { RegisterToNetsuiteComponents } from '../NEW-integrations-page-components/net-suite-components/register-to-net-suite';
import { ConfigureQuickBooksComponents } from '../NEW-integrations-page-components/quickbooks-components/configure-quickbooks-integration';
import { RegisterToQuickBooksComponents } from '../NEW-integrations-page-components/quickbooks-components/register-to-quickbooks';
import { RegisterToS3Components } from '../NEW-integrations-page-components/s3-components/register-to-s3';
import { RegisterToSalesforceComponents } from '../NEW-integrations-page-components/salesforce-components/register-to-salesforce';
import { ConfigureSlackComponents } from '../NEW-integrations-page-components/slack-components/configure-slack-integration';
import { RegisterToSlackComponents } from '../NEW-integrations-page-components/slack-components/register-to-slack';
import { ConfigurePaddleComponents } from '../NEW-integrations-page-components/paddle-components/configure-paddle-integration/ConfigurePaddleComponents';
import { RegisterToPaddleComponents } from '../NEW-integrations-page-components/paddle-components/register-to-paddle/RegisterToPaddleComponents';
import { ConfigureStripeComponents } from '../NEW-integrations-page-components/stripe-components/configure-stripe-integration';
import { RegisterToStripeComponents } from '../NEW-integrations-page-components/stripe-components/register-to-stripe';
import { ConfigureVayuApiContent } from '../NEW-integrations-page-components/vayu-api-components/ConfigureVayuApiContent';
import {
  ConfigureXeroComponents,
  RegisterToXeroComponents,
} from '../NEW-integrations-page-components/xero-components';

type IntegrationComponents = {
  integrationRegisterComponents: FormDrawerProps<ZodSchema>;
  integrationConfigurationComponents?: FormDrawerProps<ZodSchema>;
};

const integrationProviderToComponents: Record<IntegrationProviders, Optional<IntegrationComponents>> = {
  [INTEGRATION_PROVIDERS.stripe]: {
    integrationRegisterComponents: RegisterToStripeComponents,
    integrationConfigurationComponents: ConfigureStripeComponents,
  },
  [INTEGRATION_PROVIDERS.hubspot]: {
    integrationRegisterComponents: RegisterToHubspotComponents,
    integrationConfigurationComponents: ConfigureHubSpotComponents,
  },
  [INTEGRATION_PROVIDERS.netSuite]: {
    integrationRegisterComponents: RegisterToNetsuiteComponents,
    integrationConfigurationComponents: ConfigureNetSuiteComponents,
  },
  [INTEGRATION_PROVIDERS.salesforce]: {
    integrationRegisterComponents: RegisterToSalesforceComponents,
  },
  [INTEGRATION_PROVIDERS.quickBooks]: {
    integrationRegisterComponents: RegisterToQuickBooksComponents,
    integrationConfigurationComponents: ConfigureQuickBooksComponents,
  },
  [INTEGRATION_PROVIDERS.slack]: {
    integrationRegisterComponents: RegisterToSlackComponents,
    integrationConfigurationComponents: ConfigureSlackComponents,
  },
  [INTEGRATION_PROVIDERS.S3]: {
    integrationRegisterComponents: RegisterToS3Components,
  },
  [INTEGRATION_PROVIDERS.vayu]: {
    integrationConfigurationComponents: {
      ContentComponent: ConfigureVayuApiContent,
    },
    integrationRegisterComponents: {
      ContentComponent: ConfigureVayuApiContent,
    },
  },
  [INTEGRATION_PROVIDERS.morning]: {
    integrationRegisterComponents: RegisterToMorningComponents,
    integrationConfigurationComponents: ConfigureMorningComponents,
  },
  [INTEGRATION_PROVIDERS.anrok]: {
    integrationRegisterComponents: RegisterToAnrokComponents,
    integrationConfigurationComponents: ConfigureAnrokComponents,
  },
  [INTEGRATION_PROVIDERS.paddle]: {
    integrationRegisterComponents: RegisterToPaddleComponents,
    integrationConfigurationComponents: ConfigurePaddleComponents,
  },
  [INTEGRATION_PROVIDERS.snowflake]: undefined,
  [INTEGRATION_PROVIDERS.connact]: undefined,
  [INTEGRATION_PROVIDERS.xero]: {
    integrationRegisterComponents: RegisterToXeroComponents,
    integrationConfigurationComponents: ConfigureXeroComponents,
  },
};

export function useIntegrationDrawerComponents(selectedIntegration: Nullable<IntegrationProviders>) {
  const isConnectedIntegration = useActivatedIntegration(selectedIntegration);

  if (!selectedIntegration) {
    return undefined;
  }

  if (isConnectedIntegration) {
    return integrationProviderToComponents[selectedIntegration]?.integrationConfigurationComponents;
  }

  return integrationProviderToComponents[selectedIntegration]?.integrationRegisterComponents;
}
